import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";
import SEORelatedPages from "@/components/SEORelatedPages";
import { getSEOData } from "@/lib/seo-pages";

const seo = getSEOData("/pesnya-devushke-v-podarok")!;

const faqItems = [
  { q: "Сколько времени создаётся песня?", a: "Обычно около трёх минут. Вы отправляете историю, и через несколько минут получаете готовую демоверсию — с текстом, музыкой и вокалом." },
  { q: "Можно ли добавить нашу личную историю в текст?", a: "Именно так и работает. Чем больше деталей вы расскажете — имена, места, моменты — тем точнее песня попадёт в цель." },
  { q: "В каком стиле можно сделать песню?", a: "Романтическая баллада, лёгкий поп, акустика, джаз, даже нежный хип-хоп — стиль выбираете вы." },
  { q: "Что если песня нужна срочно — сегодня вечером?", a: "Демоверсия готовится за считанные минуты. Даже если праздник через час, вы успеете." },
  { q: "Можно ли внести правки после прослушивания?", a: "Да, вы можете создать новый вариант, уточнив детали или сменив стиль." },
];

const PesnyaDevushkeVPodarok = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={seo.title} description={seo.description} canonical={seo.canonical} jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqItems.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })) }} />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container px-4 mx-auto max-w-3xl">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">
            Песня девушке в подарок
          </h1>

          <div className="space-y-4 text-muted-foreground leading-relaxed mb-10">
            <p>Представьте: ресторан, приглушённый свет, она сидит напротив и ещё ничего не подозревает. Вы достаёте телефон, нажимаете «play» — и из динамика звучит <strong className="text-foreground">песня с её именем</strong>. Её глаза. Её улыбка. Тот момент, когда она понимает, что эти строчки — про неё.</p>
            <p>Это не фантазия. Это то, что происходит, когда вы дарите <strong className="text-foreground">песню девушке в подарок</strong> — настоящую, написанную по вашей истории.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Почему песня трогает сильнее цветов и украшений</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Цветы завянут. Духи закончатся. А песня останется в плейлисте — и каждый раз, включая её, она будет возвращаться в тот самый вечер.</p>
              <p>Персональная песня — это не вещь, а переживание. В ней зашиты ваши шутки, ваши общие слова, то, что понимаете только вы двое.</p>
              <p>И ещё одна деталь: такой подарок невозможно сравнить ни с чем. Потому что он существует в единственном экземпляре.</p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Пять способов вручить песню так, чтобы она расплакалась от счастья</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p><strong className="text-foreground">Ужин при свечах.</strong> Дождитесь момента после десерта. Включите трек. Пусть музыка скажет за вас.</p>
              <p><strong className="text-foreground">Утренний сюрприз.</strong> Отправьте ссылку на <Link to="/" className="text-primary hover:underline">красивую подарочную страницу</Link> первым сообщением утром.</p>
              <p><strong className="text-foreground">На <Link to="/pesnya-na-den-rozhdeniya" className="text-primary hover:underline">дне рождения</Link> при гостях.</strong> Когда все услышат её имя в песне — эффект будет невероятный.</p>
              <p><strong className="text-foreground">Во время прогулки.</strong> Парк, набережная, наушники на двоих.</p>
              <p><strong className="text-foreground">В подарочной коробке.</strong> Положите QR-код рядом с основным подарком. Подробнее о музыкальных подарках — на странице <Link to="/pesnya-v-podarok" className="text-primary hover:underline">песня в подарок</Link>.</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎵 Создать песню для неё</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Демоверсия бесплатна. Готово за 3 минуты.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">Как рождается персональная песня</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Вы рассказываете историю. Не формально — как рассказали бы другу. Выбираете стиль: мягкая акустика, романтический поп, что-то джазовое. И через несколько минут получаете готовую демоверсию.</p>
            </div>
          </section>

          <section className="mb-12">
            <h3 className="font-display text-lg sm:text-xl font-bold mb-3 text-foreground">«Я думал, она просто послушает. А она заплакала»</h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Один из пользователей готовил песню к <Link to="/pesnya-na-godovshchinu" className="text-primary hover:underline">годовщине</Link>. Три года вместе, куча совместных историй. Он написал всё, выбрал стиль — спокойный поп с акустической гитарой.</p>
              <p>Вечером включил ей. Она молча дослушала до конца. А потом сказала: «Это самое красивое, что мне когда-либо дарили».</p>
            </div>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">💌 Подготовить сюрприз, который она не забудет</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Без регистрации. Без приложений.</p>
          </div>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-foreground">Вопросы, которые задают чаще всего</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {faqItems.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card">
                  <AccordionTrigger className="text-left hover:no-underline text-sm font-medium text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="my-10 text-center">
            <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
              <Link to="/create">🎶 Пусть ваша история зазвучит</Link>
            </Button>
            <p className="text-muted-foreground text-sm mt-3">Создайте песню, которая скажет больше тысячи слов.</p>
          </div>

          <SEORelatedPages pages={seo.relatedPages || []} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PesnyaDevushkeVPodarok;
